import { Button, Checkbox, Popover, Radio, Select, Space, Switch } from "antd"
import { InfoCard } from "../common/InfoCard"
import { useEffect, useState } from "react"
import { BalancePoint } from "./common"
import { ManualCell } from "../common/ManualCell"
import { NodeState } from "@/components/FlowSheetEditor/services/modal"
import { useUpdateEffect } from "ahooks"
import { SettingOutlined } from "@ant-design/icons"
import Title from "antd/lib/typography/Title"
import isNil from "lodash/isNil";
import { useGetVariableUnit } from "@/pages/Project/hooks/useDimUnits"

interface TableCardColumn {
  title: string
  key: string
  shortTitle: string
  dimCode?: string
  render?: (col: TableCardColumn) => any
}

interface TableCardProps {
  cardClassName?: string
  unit: NodeState
  data: any
  onBPChange: (changes) => void
}

const storageKey = 'STREAM2_TABLE'

export function TableCardStream2(props: TableCardProps) {
  const { data, unit, onBPChange} = props
  const [keys, setKeys] = useState<string[]>([])
  const stream2InData = data?.vars?.S2_IN || {}
  const stream2OutData = data?.vars?.S2_OUT || {}
  const s2BPVal = data?.args?.S2_BP
  const stream2BPData = data?.vars?.S2_BP || {}
  const initDirection = unit?.simArgs?.FLOW_DIRECTION ?? 'CO_CURRENT'
  const isCoCurrent = initDirection === 'CO_CURRENT'
  const enableBP = !isNil(unit?.simArgs?.S1_BP)
  const [s2BP, setS2BP] = useState(s2BPVal ?? null)
  const getUnitEntityByCode = useGetVariableUnit()

  useUpdateEffect(() => {
    setS2BP(s2BPVal)
  }, [s2BPVal])

  const columns: TableCardColumn[] = [
    {
      title: 'Temperature (T)',
      key: 'T',
      shortTitle: 'T',
      dimCode: 'TEMP',
      render: (col) => {
        return <ManualCell cellData={stream2InData.F} readonly={true}  />
      }
    },
    {
      title: 'Pressure (P)',
      key: 'P',
      dimCode: 'PRES',
      shortTitle: 'P',
      render: (col) => {
        return <ManualCell cellData={stream2InData.P} readonly={true}  />
      }
    },
    {
      title: 'Vapor Fraction (VF)',
      key: 'VF',
      shortTitle: 'VF',
      render: (col) => {
        return <ManualCell cellData={stream2InData.VF} readonly={true}  />
      },
      dimCode: 'PF'
    },
    {
      title: 'Enthalpy (h)',
      key: 'h',
      shortTitle: 'h',
      render: (col) => {
        return <ManualCell cellData={stream2InData.h} readonly={true}  />
      },
      dimCode: 'MOL_H',
    },
  ]

  useEffect(() => {
    const keyStr = localStorage.getItem(storageKey)
    if (keyStr) {
      setKeys(JSON.parse(keyStr))
    } else {
      setKeys(columns.map(item => item.key))
    }
  }, [])

  const onColumnsChange = (values) => {
    setKeys(values)
    localStorage.setItem(storageKey, JSON.stringify(values))
  }

  const columnOptions = columns.map(item => {
    return {
      label: item.title,
      key: item.key,
      value: item.key
    }
  })

  const otherColumns = columns.filter(item => {
    if (keys.length === 0) {
      return true;
    }
    return keys.includes(item.key)
  })

  const renderCell = (col: TableCardColumn, data) => {
    const {key, dimCode} = col;
    const cellData = data[key]
    const unitEntiry = getUnitEntityByCode(dimCode)

    return <ManualCell cellData={cellData} readonly={true} dimCode={dimCode} unitEntiry={unitEntiry}  />
  }

  const renderTop = (col: TableCardColumn) => {
    const topData = isCoCurrent ? stream2InData : stream2OutData
    return renderCell(col, topData)
  }

  const renderBottom = (col: TableCardColumn) => {
    const bottomData = isCoCurrent ? stream2OutData : stream2InData
    return renderCell(col, bottomData)
  }

  const renderPoint = (col: TableCardColumn) => {
    return renderCell(col, stream2BPData)
  }

  const setBalancePoint = (val) => {
    const simArgs = {...(unit.simArgs || {})}
    simArgs.S2_BP = val
    onBPChange(simArgs)
    setS2BP(val)
  }

  const popoverContent = (<>
    <Checkbox.Group
      onChange={onColumnsChange}
      value={keys}
      className="d-block"
    >
      {columnOptions.map(option => {
        return <div key={option.key}>
          <Checkbox value={option.value}>{option.label}</Checkbox>
        </div>
      })}
    </Checkbox.Group>
  </>
  )
  
  return <InfoCard
    title={'Stream2'}
    className='mt-1'
    action={<Space>
      
      <Popover content={popoverContent} trigger={'click'} title={<Title level={5}>Stream2 Display columns</Title>}>
        <Button icon={<SettingOutlined />} />
      </Popover>
    </Space>}
  >
    <table style={{width: '100%'}} className="bordered-table break-word-table">
      <thead>
        <tr>
          <th scope="col" style={{width: 140}}>Point</th>
          {otherColumns.map((col) => {
            let title = col.shortTitle
            if (col.dimCode) {
              const unitEntiry = getUnitEntityByCode(col.dimCode)
              if (unitEntiry) {
                title = `${title}[${unitEntiry.unit?.code || '-'}]`
              }
            }
            return <th scope="col" key={col.key}>{title}</th>
          })}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            {isCoCurrent ? 'Inlet' : 'Outlet'}
          </td>
          {otherColumns.map((col) => {
            return <td scope="col" key={col.key}>{renderTop(col)}</td>
          })}
        </tr>
        {enableBP && <tr>
          <td>
            <Select value={s2BP} style={{width: '100%'}} onChange={setBalancePoint}>
              <Select.Option value={BalancePoint.Calculated}>Calculated</Select.Option>
              <Select.Option value={BalancePoint.BoilingPoint}>Boiling Point</Select.Option>
              <Select.Option value={BalancePoint.DewPoint}>Dew Point </Select.Option>
            </Select>
          </td>
          {otherColumns.map((col) => {
            return <td scope="col" key={col.key}>{renderPoint(col)}</td>
          })}
        </tr>}
        <tr>
          <td>
            {isCoCurrent ? 'Outlet' : 'Inlet'}
          </td>
          {otherColumns.map((col) => {
            return <td scope="col" key={col.key}>{renderBottom(col)}</td>
          })}
        </tr>
      </tbody>
    </table>
  </InfoCard>
}
